import React from 'react';

import { useTranslation } from 'react-i18next';

import { ContentStepWritable, Plan } from '@cfa/pathway-fe-shared/xp';
import { Button } from '@cfa/react-core';

import { useUpdatePlan } from '@/hooks/useUpdatePlan';

import Constants from '@/constants';

import { PopupDocument } from '../types';

interface AddProcedureFooterProps {
  plan?: Plan;
  isInSection: boolean;
  sectionId: string;
  adminAddingProcedure: boolean;
  onClose: () => void;
  selectedDocuments: PopupDocument[];
  setSelectedDocuments: (documents: PopupDocument[]) => void;
  onCustomAddProcedure?: (selectedDocuments: PopupDocument[]) => void;
  textVariant: 'update' | 'default';
}

export const AddProcedureFooter: React.FC<AddProcedureFooterProps> = ({
  plan,
  isInSection,
  sectionId,
  adminAddingProcedure,
  onClose,
  selectedDocuments,
  setSelectedDocuments,
  onCustomAddProcedure,
  textVariant,
}) => {
  const { t } = useTranslation();
  const { addSteps, isPending } = useUpdatePlan({
    onSuccess: () => {
      onClose();
      setSelectedDocuments([]);
    },
  });

  const onAddProcedure = async () => {
    if (!plan) return;
    const steps: ContentStepWritable[] = selectedDocuments.map(doc => ({
      id: doc.id,
      name: doc.name,
      reference: doc.reference.id,
      type: Constants.STEP_TYPES.DOCUMENT,
    }));
    await addSteps(plan, steps, isInSection ? sectionId : undefined);
  };

  const currentCount = selectedDocuments.length;

  const handleCustomAddProcedure = () => {
    onCustomAddProcedure?.(selectedDocuments);
    onClose();
  };

  return (
    <Button
      color="secondary"
      data-testid="addProcedureButton"
      isDisabled={currentCount === 0 || isPending}
      isPending={isPending}
      onPress={
        adminAddingProcedure || !!onCustomAddProcedure
          ? handleCustomAddProcedure
          : onAddProcedure
      }
    >
      {textVariant === 'update'
        ? t('Generic.chooseResource')
        : t('Button.addXObjects', {
            count: currentCount,
            object:
              currentCount === 1
                ? t('Generic.resource')
                : t('Generic.resources'),
          })}
    </Button>
  );
};
